import { useEffect, useState } from "react";
import api, { resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Plus, Edit3, Trash2, Upload, X } from "lucide-react";

const empty = () => ({ name: "", role: "", image: "", bio: "", order: 0 });

export default function AdminTeam() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/team");
      setItems(r.data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Erreur de chargement");
    }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setEditing(null); setForm(empty()); };
  const onEdit = (it) => { setEditing(it); setForm({ ...empty(), ...it }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, order: parseInt(form.order) || 0 };
      if (editing) {
        await api.patch(`/admin/team/${editing.id}`, payload);
        toast.success("Membre mis à jour");
      } else {
        await api.post("/admin/team", payload);
        toast.success("Membre ajouté");
      }
      setOpen(false); reset(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce membre ?")) return;
    await api.delete(`/admin/team/${id}`);
    toast.success("Supprimé");
    load();
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: r.data.url }));
      toast.success("Image uploadée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload échoué");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-team-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-bold text-2xl md:text-3xl text-[var(--dd-green)]">Équipe Dakar Street Food</h1>
          <p className="text-[var(--dsf-text-muted)] text-sm mt-1">{items.length} membre(s) — visibles sur la home page et la page À propos</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogTrigger asChild>
            <button className="btn-mustard !py-2.5" data-testid="add-team-button"><Plus size={14} /> Nouveau membre</button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-bold text-2xl text-[var(--dd-green)]">{editing ? "Modifier" : "Ajouter"} un membre</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-4">
              <div><Label>Nom complet</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="team-form-name" /></div>
              <div><Label>Rôle / Poste</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1" placeholder="Executive Chef, Service Director…" data-testid="team-form-role" /></div>
              <div>
                <Label>Bio (optionnel)</Label>
                <textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="w-full border border-black/10 rounded-xl px-3 py-2 mt-1 text-sm" />
              </div>
              <div className="border-2 border-dashed border-black/10 rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-2 text-[var(--dd-green)] font-semibold"><Upload size={16} /> Photo</div>
                <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="URL externe" />
                <input type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files?.[0])} className="text-xs" disabled={uploading} data-testid="team-form-image-upload" />
                {form.image && <img src={resolveImage(form.image)} alt="preview" className="h-28 w-28 rounded-full object-cover ring-4 ring-[var(--dd-mustard)]/30" />}
              </div>
              <div><Label>Ordre d'affichage</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1" placeholder="0 = en premier" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-[var(--dsf-border)]">
              <button onClick={() => setOpen(false)} className="btn-pill-outline">Annuler</button>
              <button onClick={save} className="btn-mustard" data-testid="team-form-submit">{editing ? "Mettre à jour" : "Créer"}</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {items.length === 0 && <div className="col-span-full text-center py-16 text-[var(--dsf-text-muted)]">Aucun membre — ajoutez le premier</div>}
        {items.map((it) => (
          <div key={it.id} className="bg-white border border-[var(--dsf-border)] rounded-2xl overflow-hidden text-center" data-testid={`team-row-${it.id}`}>
            <div className="aspect-square bg-[var(--dd-bone)] flex items-center justify-center">
              {it.image ? <img src={resolveImage(it.image)} alt={it.name} className="w-full h-full object-cover" /> : <div className="text-[var(--dsf-text-muted)] text-sm">No photo</div>}
            </div>
            <div className="p-4">
              <div className="font-bold text-[var(--dd-green)]">{it.name}</div>
              <div className="text-xs text-[var(--dsf-text-muted)] mb-3">{it.role}</div>
              <div className="flex gap-2">
                <button onClick={() => onEdit(it)} className="flex-1 btn-pill-outline !py-1.5 !text-xs" data-testid={`team-edit-${it.id}`}><Edit3 size={12} /> Modifier</button>
                <button onClick={() => remove(it.id)} className="w-9 h-9 rounded-full border border-[var(--dsf-border)] flex items-center justify-center text-[var(--dd-coral)]" data-testid={`team-delete-${it.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
